import { useState } from "react";
import { photosApi, ApiError } from "@/api";
import type { DrawingSet, PhotoSet, PhotoSetUpdateInput } from "@/types";
import { Button, TextField, SelectField } from "@/components/ui";
import { toDateInputValue } from "@/lib/format";
import { FormDrawer } from "./FormDrawer";

/** Create or edit a photo set (a phase / visit). Label, visit date and an
 *  optional link to one of the job's production drawing sets. Delete lives
 *  here too, offered only while the set is empty (the server 400s otherwise).
 *  Edits send only what changed; unlinking the drawing set rides the
 *  clear_drawing_set bool because a 0 never reaches a Xano stack. */
export function PhotoSetDrawer({
  jobId,
  jobNumber,
  drawingSets,
  set,
  onClose,
  onSaved,
  onDeleted,
}: {
  jobId: number;
  jobNumber: number;
  drawingSets: DrawingSet[];
  /** null = create. */
  set: PhotoSet | null;
  onClose: () => void;
  onSaved: (saved: PhotoSet) => void;
  onDeleted: (deleted: PhotoSet) => void;
}) {
  const editing = set != null;
  const [label, setLabel] = useState(set?.label ?? "");
  const [visitDate, setVisitDate] = useState(toDateInputValue(set ? set.visit_date : Date.now()));
  const [drawingSetId, setDrawingSetId] = useState<number>(set?.drawing_set_id ?? 0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /** "yyyy-mm-dd" → midnight UTC ms (the server normalises anyway). */
  function dateToMs(v: string): number | undefined {
    if (!v || v.length !== 10) return undefined;
    const t = Date.UTC(Number(v.slice(0, 4)), Number(v.slice(5, 7)) - 1, Number(v.slice(8, 10)));
    return Number.isFinite(t) ? t : undefined;
  }

  async function submit() {
    if (saving || deleting) return;
    setSaving(true);
    setError(undefined);
    try {
      const visit = dateToMs(visitDate);
      const trimmed = label.trim();
      if (editing && set) {
        const body: PhotoSetUpdateInput = {};
        if (trimmed && trimmed !== set.label) body.label = trimmed;
        if (visit && visit !== set.visit_date) body.visit_date = visit;
        if (drawingSetId > 0 && drawingSetId !== set.drawing_set_id) body.drawing_set_id = drawingSetId;
        if (drawingSetId === 0 && set.drawing_set_id > 0) body.clear_drawing_set = true;
        onSaved(await photosApi.updatePhotoSet(set.id, body));
      } else {
        onSaved(
          await photosApi.createPhotoSet({
            job_id: jobId,
            label: trimmed || undefined,
            visit_date: visit,
            drawing_set_id: drawingSetId > 0 ? drawingSetId : undefined,
          }),
        );
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the set.");
      setSaving(false);
    }
  }

  async function remove() {
    if (!set || saving || deleting) return;
    if (!window.confirm(`Delete the empty set “${set.label}”?`)) return;
    setDeleting(true);
    setError(undefined);
    try {
      await photosApi.deletePhotoSet(set.id);
      onDeleted(set);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete the set.");
      setDeleting(false);
    }
  }

  return (
    <FormDrawer
      title={editing ? "Edit photo set" : "New photo set"}
      busy={saving || deleting}
      onClose={onClose}
      footer={
        <>
          {editing && set && set.photo_count === 0 ? (
            <Button variant="danger" size="sm" className="job-task-del" onClick={() => void remove()} loading={deleting} disabled={saving}>
              Delete set
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose} disabled={saving || deleting}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving} disabled={deleting}>
            {editing ? "Save" : "Create set"}
          </Button>
        </>
      }
    >
      <TextField
        label="Label"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="e.g. Existing survey, Demolition, First fix"
        hint="Leave blank for “Site visit {date}”."
        autoFocus
      />
      <TextField label="Visit date" type="date" value={visitDate} onChange={(e) => setVisitDate(e.target.value)} />
      <SelectField
        label="Drawing set"
        value={String(drawingSetId)}
        onChange={(e) => setDrawingSetId(Number(e.target.value))}
        hint="Optional — ties this phase to the drawing set it relates to."
      >
        <option value="0">Not linked</option>
        {drawingSets.map((s) => (
          <option key={s.id} value={String(s.id)}>
            {jobNumber}-{s.set_number}
            {s.label ? ` · ${s.label}` : ""}
          </option>
        ))}
      </SelectField>
      {editing && set && set.photo_count > 0 ? (
        <p className="job-drawer__muted">
          {set.photo_count} photo{set.photo_count === 1 ? "" : "s"} in this set. Move or delete them before deleting the set.
        </p>
      ) : null}
      {error ? (
        <p className="photos__error" role="alert">
          {error}
        </p>
      ) : null}
    </FormDrawer>
  );
}
